"use client";

import { cn } from "@/lib/utils";
import { MoveLeft, MoveRight } from "lucide-react";
import { useRouter } from "next/navigation";
import type React from "react";
import { v4 } from "uuid";
import { default as Pager } from "./Pager";

const pagingConfig = {
	previous: "Previous",
	next: "Next",
};

interface PaginatorProps {
	page: number;
	totalPages: number;
	baseUrl: string;
	pageUrl: string;
	className?: string;
}

const Paginator: React.FC<PaginatorProps> = ({
	page,
	totalPages,
	baseUrl,
	pageUrl,
	className,
}) => {
	const router = useRouter();
	const prevLink =
		page > 2 ? baseUrl + pageUrl + (page - 1).toString() : baseUrl;
	const nextLink =
		page < totalPages
			? baseUrl + pageUrl + (page + 1).toString()
			: baseUrl + pageUrl + page.toString();

	// Artificial for loop for JSX or TSX
	// It will be used for pagination
	const rows: Array<number> = [];
	let i = 0;
	while (++i <= totalPages) rows.push(i);

	// End of pagination for loop function
	return (
		<nav className={cn("w-full flex items-center justify-between", className)}>
			<div className="flex w-0 flex-1">
				<button
					type="button"
					onClick={() => {
						router.push(prevLink);
						router.refresh();
					}}
					className={cn(
						{
							"pointer-events-none opacity-50": page === 1,
						},
						"inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-primary hover:bg-primary-34/10 hover:text-primary",
					)}
				>
					<MoveLeft className="mr-3 h-5 w-5 text-primary" aria-hidden="true" />
					{pagingConfig.previous}
				</button>
			</div>
			{/* Pagination */}
			<div className="hidden gap-2 md:-mt-px md:flex">
				{Array(totalPages)
					.fill(1)
					.map((el, i) => (
						<Pager
							key={v4()}
							index={i}
							totalPages={totalPages}
							currentPage={page}
							baseUrl={baseUrl}
							pageUrl={pageUrl}
						/>
					))}
			</div>

			<div className="-mt-px flex w-0 flex-1 justify-end">
				<button
					type="button"
					onClick={() => {
						router.push(nextLink);
						router.refresh();
					}}
					className={cn(
						"inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-primary hover:bg-primary-34/10 hover:text-primary",
						{ "pointer-events-none opacity-50": page >= totalPages },
					)}
				>
					{pagingConfig.next}
					<MoveRight className="ml-3 h-5 w-5 text-primary" aria-hidden="true" />
				</button>
			</div>
		</nav>
	);
};

export default Paginator;
